import React, { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import { CustomTable } from '../../../components';
import {
  COLUMNS,
  EDITABLE_STATUSES,
  EMPTY_TABLE_MESSAGE,
  PAGE_SIZE_OPTIONS,
} from '../constants';
import type { AssistedAffiliationTableProps } from '../types';
import StatusChip from './StatusChip';
import useStyle from '../styles';

const AssistedAffiliationTable = ({
  requests,
  readOnly = false,
  loading = false,
  validatingRequestId = null,
  onEdit,
  totalElements = 0,
  onNextPage,
  onPageSizeChange,
}: AssistedAffiliationTableProps) => {
  const classes = useStyle();

  // Referencia estable: ver `CustomTableProps.data`.
  const rows = useMemo(() => requests.map((request) => ({
    id: request.id,
    active: true,
    registryNumber: request.registryNumber,
    createdAt: new Date(request.createdAt).getTime(),
    identification: request.identification,
    commercialName: request.commercialName,
    legalRepresentative: request.legalRepresentative,
    status: <StatusChip status={request.status} />,
    bhdExecutive: request.bhdExecutive,
    commercialAssignee: request.commercialAssignee,
    actions: (
      <Box display="flex" justifyContent="center">
        <Tooltip title="Ver">
          <IconButton size="small" aria-label={`ver-${request.registryNumber}`}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <span>
            <IconButton
              size="small"
              aria-label={`editar-${request.registryNumber}`}
              disabled={
                readOnly ||
                !EDITABLE_STATUSES.includes(request.status) ||
                validatingRequestId === request.id
              }
              onClick={() => onEdit?.(request)}
            >
              {validatingRequestId === request.id ? (
                <CircularProgress size={18} />
              ) : (
                <EditIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
  })), [requests, readOnly, validatingRequestId, onEdit, classes]);

  // Sin esta condición el estado vacío parpadea mientras la consulta está en vuelo.
  if (loading) {
    return (
      <Box className={classes.emptyState}>
        <CircularProgress />
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          {EMPTY_TABLE_MESSAGE}
        </Typography>
      </Box>
    );
  }

  return (
    <CustomTable
      data={rows}
      columns={COLUMNS}
      readOnly={readOnly}
      totalElementsData={totalElements}
      onNext={onNextPage}
      onSelectPageSize={onPageSizeChange}
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
    />
  );
};

export default AssistedAffiliationTable;
